import { DiscordSDK, patchUrlMappings } from '@discord/embedded-app-sdk';

/** Single Discord SDK boot path; memoizes a Promise keyed by clientId, retries 3× with expBackoff. */

export type ReadyDiscordSdk = {
  sdk: DiscordSDK;
  instanceId: string;
};

const MAX_RETRIES = 3;
const RETRY_BACKOFF_MS = [200, 400, 800] as const;

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

let pending: Promise<ReadyDiscordSdk> | null = null;
let pendingClientId: string | null = null;

const bootOnce = async (clientId: string): Promise<ReadyDiscordSdk> => {
  const sdk = new DiscordSDK(clientId);
  await sdk.ready();
  // Route Forge Stats through `/.proxy/forge-stats` so CSP doesn't reject direct fetches.
  patchUrlMappings([{ prefix: '/forge-stats', target: 'api-events.forge.codeserk.es' }]);
  return { sdk, instanceId: sdk.instanceId };
};

const bootWithRetry = async (clientId: string): Promise<ReadyDiscordSdk> => {
  let lastErr: unknown;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt += 1) {
    try {
      return await bootOnce(clientId);
    } catch (err) {
      lastErr = err;
      const delay = RETRY_BACKOFF_MS[attempt] ?? RETRY_BACKOFF_MS[RETRY_BACKOFF_MS.length - 1]!;
      await sleep(delay);
    }
  }
  // Wipe cached promise so a Retry button can re-attempt.
  pending = null;
  pendingClientId = null;
  throw new Error(
    `discord_unavailable: SDK boot failed after ${MAX_RETRIES} attempts (${
      lastErr instanceof Error ? lastErr.message : String(lastErr)
    })`,
  );
};

export const ensureDiscordSdk = (clientId: string): Promise<ReadyDiscordSdk> => {
  if (!clientId) {
    return Promise.reject(
      new Error('discord_unavailable: VITE_DISCORD_CLIENT_ID is not configured'),
    );
  }
  if (pending && pendingClientId === clientId) return pending;
  pendingClientId = clientId;
  pending = bootWithRetry(clientId);
  return pending;
};

/** Discard cached SDK promise so the next `ensureDiscordSdk` starts fresh. */
export const resetDiscordSdk = (): void => {
  pending = null;
  pendingClientId = null;
};
